import { useContext, useEffect } from 'react';

import { FormContext } from '../../contexts';
import { FormInitialValues } from '../../typings';
import { useFieldValue, useFormChangeTracker } from '@/forms/hooks';
import { useAppDispatch } from '@/hooks';
import { setConditionalFields } from '@/forms/store';
import { FormFieldMapper } from '../FormFieldMapper';

type LoginFormPresenterProps = {
  onFormChange?: (changedValuesObject: FormInitialValues) => void;
  uniqueKey: string;
};

// Define the fields to track in a single array
const FIELDS_TO_TRACK = [
  'identity',
  'potentialTerminationReason',
  'denialReason',
  'retirementDate',
  'details',
  'knownNewCredentialingContact',
  'newCredentialingContactDetails',
  'newCredentialingContactName',
  'newCredentialingContactPhone',
  'newCredentialingContactEmail',
  'firstName',
  'lastName',
  'representativeFirstName',
  'representativeLastName',
];

export const LoginFormPresenter: React.FC<LoginFormPresenterProps> = ({
  onFormChange,
  uniqueKey,
}) => {
  const { values, initialValues, dirty, formFieldGroups, reinitializeCount } =
    useContext(FormContext);

  useFormChangeTracker(
    uniqueKey,
    values,
    dirty,
    onFormChange,
    reinitializeCount,
    initialValues,
  );

  const dispatch = useAppDispatch();

  // Create a reusable effect for each field
  const useFieldValueEffect = (fieldName: string) => {
    const value = useFieldValue(fieldName);

    useEffect(() => {
      if (value !== undefined) {
        dispatch(setConditionalFields({ [fieldName]: value }));
      }
    }, [value, fieldName]);
  };

  // Apply the effect to each field
  FIELDS_TO_TRACK.forEach(useFieldValueEffect);

  return formFieldGroups.map(fieldGroup => (
    <FormFieldMapper key={fieldGroup.fieldId} field={fieldGroup} />
  ));
};
